import { type ComponentType, type SVGProps } from 'react';

import { type IAppProtectionAssuranceSummaryQuery } from 'src/graphql/actions/apps/appProtectionAssuranceSummary.query';
import { IconBadgeCertified, IconBadgeValidated, IconBadgeVerified } from 'src/components/Svg/Images';

export type IAssuranceSummary = IAppProtectionAssuranceSummaryQuery['appProtectionAssuranceSummary'];

type IAssuranceSummaryField = Exclude<keyof IAssuranceSummary, '__typename'>;

export type IAssuranceLevel = {
  key: string;
  label: string;
  description: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  barClassName: string;
  countField: IAssuranceSummaryField;
  percentageField: IAssuranceSummaryField;
  // Protection counts that add up to the protections able to hold this level.
  eligibleFields: IAssuranceSummaryField[];
  eligibleNoun: string;
};

// Ordered from the lowest to the highest level; each level includes the checks of the ones before it.
export const ASSURANCE_LEVELS: IAssuranceLevel[] = [
  {
    key: 'validated',
    label: 'Validated',
    description: 'Automated validation that shows which assets are included in your software protections.',
    icon: IconBadgeValidated,
    barClassName: 'bg-assurance-validated',
    countField: 'certificationValidatedCount',
    percentageField: 'certificationValidatedPercentage',
    eligibleFields: ['backupsCount', 'custodianProfilesCount', 'escrowsCount'],
    eligibleNoun: 'protections',
  },
  {
    key: 'verified',
    label: 'Verified',
    description: 'Automated review of all uploaded materials and any updates made to them.',
    icon: IconBadgeVerified,
    barClassName: 'bg-assurance-verified',
    countField: 'certificationVerifiedCount',
    percentageField: 'certificationVerifiedPercentage',
    eligibleFields: ['backupsCount', 'escrowsCount'],
    eligibleNoun: 'escrows and backups',
  },
  {
    key: 'certified',
    label: 'Certified',
    description:
      'In-depth human analyses that give you complete assurance of software resilience. Only available on escrows.',
    icon: IconBadgeCertified,
    barClassName: 'bg-assurance-certified',
    countField: 'certificationCertifiedCount',
    percentageField: 'certificationCertifiedPercentage',
    eligibleFields: ['escrowsCount'],
    eligibleNoun: 'escrows',
  },
];
